import { useEffect } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import AppToast from "../../../shared/components/AppToast";
import CatalogBreadcrumb from "../components/CatalogBreadcrumb";
import ProductGallery from "../components/ProductGallery";
import { getCompany } from "../../../config/companyConfig";
import { formatCLP } from "../../../utils/currency";
import useMobile from "../../../utils/useMobile";
import logger from "../../../shared/utils/logger";
import "../styles/product-detail.css";
import useProductDetail from "../hooks/useProductDetail";

const ProductDetail = () => {
    const isMobileView = useMobile();

    const {
        code,
        displayedProduct,
        fichaData,
        fichaPdfData,
        breadcrumbModel,
        productImageSrc,
        hasDiscount,
        formattedPrice,
        formattedRegularPrice,
        finalUnitPriceValue,
        selectedSucursalName,
        warehouseOptions,
        selectedAlmacen,
        setSelectedAlmacen,
        productLoading,
        productError,
        quantity,
        setQuantity,
        addingToCart,
        isAuthenticated,
        fetchProduct,
        handleAddToCart,
        handleProductImageError,
        toastRef,
    } = useProductDetail();

    useEffect(() => { fetchProduct(); }, [fetchProduct]);

    const handleVerPdf = () => {
        if (!fichaPdfData?.pdf_base64) return;
        try {
            const byteString = atob(fichaPdfData.pdf_base64);
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
            const blob = new Blob([ab], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank");
        } catch (err) {
            logger.error("Error al abrir PDF:", err);
            toastRef.current?.show({
                severity: "error",
                summary: "Error",
                detail: "No se pudo abrir el PDF.",
                life: 4000,
            });
        }
    };

    if (isMobileView) {
        return (
            <div className="landing-container product-detail-page product-detail-mobile">
                <AppToast ref={toastRef} />
                <CatalogBreadcrumb items={breadcrumbModel} homeTo="/catalogo" className="product-detail-breadcrumb product-detail-mobile-breadcrumb" />

                <div className="product-detail-mobile-stock-branch-row">
                    <div className="product-detail-mobile-media col-6">
                        {hasDiscount && <div className="product-detail-mobile-discount"><strong>{displayedProduct.descuento}%</strong><span>DCTO</span></div>}
                        <ProductGallery
                            fotos={displayedProduct.fotos}
                            image={productImageSrc}
                            alt={displayedProduct.descripcion}
                            className="product-detail-carousel"
                            onError={handleProductImageError}
                        />
                    </div>
                    <div className="product-detail-mobile-info col-6">
                        <div className="product-detail-mobile-meta">
                            <span>{displayedProduct.tipo}</span>
                            <span className="product-detail-mobile-meta-divider">|</span>
                            <span>SKU: {displayedProduct.codigo || code}</span>
                        </div>
                        <h1 className="product-detail-mobile-name">{fichaData?.descripcion || displayedProduct.descripcion}</h1>
                        {isAuthenticated && (
                            <div className={`product-detail-mobile-price-block ${hasDiscount ? "has-discount" : ""}`}>
                                <div className="product-detail-mobile-price-row">
                                    <span className="product-detail-mobile-price">{formattedPrice}</span>
                                    {displayedProduct.unidadMedida && <span className="product-detail-mobile-price-unit">{displayedProduct.unidadMedida}</span>}
                                </div>
                                {formattedRegularPrice && <div className="product-detail-mobile-regular-price">Normal {formattedRegularPrice}</div>}
                            </div>
                        )}
                    </div>
                </div>

                <div className="product-detail-mobile-info">
            
                    {fichaData?.descripcion && (
                        <div className="product-detail-section">
                            <h3>Descripción</h3>
                            <p className="product-detail-section">{displayedProduct.descripcion}</p>
                        </div>
                    )}
                    {fichaData?.aplicacion && (
                        <div className="product-detail-section">
                            <h3>Aplicación</h3>
                            <p>{fichaData.aplicacion}</p>
                        </div>
                    )}
                    {fichaPdfData && (
                        <button
                            type="button"
                            className="flex align-items-center gap-2 text-sm font-semibold text-primary mt-2"
                            onClick={handleVerPdf}
                            style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                        >
                            <i className="pi pi-file-pdf"></i>
                            Ver Ficha Técnica
                        </button>
                    )}
                    {productLoading && <div className="product-detail-mobile-loading">Actualizando stock y precio...</div>}
                    {productError && <div className="product-detail-mobile-error">{productError}</div>}
                </div>

                <div className="p-2 border-1 border-round surface-border bg-white mt-2">
                    <div className="flex align-items-center gap-2 mb-2"><span className="font-bold text-lg">Disponibilidad: </span>
                        { displayedProduct.stock > 0 ? (
                                <Button size="medium" label="Stock Disponible" outlined severity="success"/>    
                            ) : (
                                <Button size="medium" label="Stock No Disponible" outlined severity="danger"/>    
                            )
                        }
                    </div>

                    <div className="flex align-items-center gap-2"><span className="font-bold text-lg">Comuna</span>
                        {warehouseOptions.length > 1 && (
                            <Dropdown
                                value={selectedAlmacen}
                                options={warehouseOptions}
                                onChange={(e) => setSelectedAlmacen(e.value)}
                                placeholder="Seleccionar sucursal"
                            />
                        )}
                    </div>
                </div>

                {isAuthenticated && (
                    displayedProduct.stock > 0 ? (
                        <>
                            <div className="product-detail-mobile-actions">
                                <div className="product-detail-mobile-qty">
                                    <span className="product-detail-mobile-qty-label">Cantidad</span>
                                    <div className="product-detail-mobile-qty-selector">
                                        <button type="button" className="product-detail-mobile-qty-btn" onClick={() => setQuantity((p) => Math.max(1, p - 1))} aria-label="Disminuir"><i className="pi pi-minus" /></button>
                                        <input className="product-detail-mobile-qty-input" type="number" min="1" value={quantity} onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))} />
                                        <button type="button" className="product-detail-mobile-qty-btn" onClick={() => setQuantity((p) => p + 1)} aria-label="Aumentar"><i className="pi pi-plus" /></button>
                                    </div>
                                    <Button size="large" onClick={handleAddToCart} disabled={addingToCart} icon="pi pi-shopping-cart" label={addingToCart ? "Agregando..." : "Agregar al carro"}/>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="product-detail-mobile-actions">
                            <Button size="large" label="Cotizar" icon="pi pi-whatsapp" onClick={() => window.open(getCompany().whatsappUrl, "_blank")} />
                        </div>
                    )
                )}
                
            </div>
        );
    }

    return (
        <div className="landing-container product-detail-page">
            <AppToast ref={toastRef} />
            <CatalogBreadcrumb items={breadcrumbModel} homeTo="/catalogo" className="product-detail-breadcrumb ml-3" />

            <div className="product-detail-layout">
                <div className="product-detail-media ml-3">
                    {hasDiscount && <div className="product-detail-discount-badge"><strong>{displayedProduct.descuento}%</strong><span>DCTO</span></div>}
                    <ProductGallery
                        fotos={displayedProduct.fotos}
                        image={productImageSrc}
                        alt={displayedProduct.descripcion}
                        className="product-detail-carousel"
                        onError={handleProductImageError}
                    />
                </div>

                <div className="product-detail-info">
                    <div className="product-detail-mobile-certified ml-1"><i className="pi pi-check" /><span>Producto Certificado</span></div>
                    <span className="p-text-secondary text-xs uppercase tracking-wider">{displayedProduct.tipo}</span>
                    <h1 className="text-2xl font-bold m-0">{fichaData?.descripcion || displayedProduct.descripcion}</h1>

                    {isAuthenticated && (
                        <div className={`product-detail-price-block ${hasDiscount ? "has-discount" : ""}`}>
                            <div className="flex align-items-center gap-2">
                                <span className="text-2xl font-bold">{formattedPrice}</span>
                                {displayedProduct.descuento > 0 && <Tag value={`-${displayedProduct.descuento}%`} severity="success" />}
                                {formattedRegularPrice && <span className="p-text-secondary text-sm font-bold product-detail-regular-price">Normal {formattedRegularPrice}</span>}
                            </div>
                            {hasDiscount && <span className="text-sm font-bold text-green-600">Ahorras {formatCLP((displayedProduct.precio - finalUnitPriceValue) * quantity)}</span>}
                        </div>
                    )}

                    {fichaData?.descripcion && (
                        <div className="mt-2">
                            <h3 className="text-base font-bold m-0 mb-2">Descripción</h3>
                            <p className="p-text-secondary text-sm m-0 line-height-2">{displayedProduct.descripcion}</p>
                        </div>
                    )}
                    {fichaData?.aplicacion && (
                        <div className="mt-2">
                            <h3 className="text-base font-bold m-0 mb-2">Aplicación</h3>
                            <p className="p-text-secondary text-sm m-0 line-height-2">{fichaData.aplicacion}</p>
                        </div>
                    )}

                    <span className="p-text-secondary">SKU: {displayedProduct.codigo || code}</span>



                    <a className="flex align-items-center gap-2 text-sm font-semibold text-primary cursor-pointer mt-2" href={getCompany().catalogPdf} target="_blank" rel="noreferrer noopener">
                        <i className="pi pi-download"></i>Descargar Catálogo SACK
                    </a>
                    {fichaPdfData && (
                        <button
                            type="button"
                            className="flex align-items-center gap-2 text-sm font-semibold text-primary cursor-pointer mt-2"
                            onClick={handleVerPdf}
                            style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                        >
                            <i className="pi pi-file-pdf"></i>
                            Ver Ficha Técnica
                        </button>
                    )}
                    <div className="flex align-items-center gap-2 mt-2 p-3 border-1 border-round surface-border surface-50">
                        <i className="pi pi-comments text-primary"></i>
                        <div className="flex flex-column">
                            <strong className="text-sm">¿Necesitas ayuda?</strong>
                            <a className="text-sm text-primary font-semibold" href={getCompany().whatsappUrl} target="_blank" rel="noreferrer noopener">Habla con un especialista</a>
                        </div>
                    </div>

                    {productLoading && <div className="text-sm p-text-secondary mt-2">Actualizando stock y precio por sucursal...</div>}
                    {productError && <div className="text-sm text-red-500 mt-2">{productError}</div>}
                </div>

                <div className="product-detail-actions-col">
                    {isAuthenticated && (
                        displayedProduct.stock > 0 ? (
                            <>
                                <div className="flex flex-column gap-2">
                                    <h3 className="text-base font-bold m-0 mb-2">Cantidad</h3>
                                    <div className="flex align-items-cente justify-content-between gap-2 mb-2">
                                        <Button icon="pi pi-minus" className="p-button-outlined" onClick={() => setQuantity((p) => Math.max(1, p - 1))} />
                                        <InputNumber value={quantity} onValueChange={(e) => setQuantity(Math.max(1, e.value || 1))} min={1} showButtons={false} className="text-center font-bold" inputClassName="text-center font-bold" />
                                        <Button icon="pi pi-plus" className="p-button-outlined" onClick={() => setQuantity((p) => p + 1)} />
                                    </div>
                                    <div className="flex justify-content-between align-items-center">
                                        <span className="text-sm p-text-secondary">Subtotal</span>
                                        <span className="text-base font-bold">{formatCLP(finalUnitPriceValue * quantity)}</span>
                                    </div>
                                </div>
                                <Button className="w-full" icon="pi pi-shopping-cart" label={addingToCart ? "Agregando..." : "Agregar al carro"} onClick={handleAddToCart} disabled={addingToCart} />
                            </>
                        ) : (
                            <Button className="w-full" label="Cotizar" icon="pi pi-comments" onClick={() => window.open(getCompany().whatsappUrl, "_blank")} />
                        )
                    )}

                    <div className="p-2 border-1 border-round surface-border bg-white mt-2">

                        <div className="flex align-items-center gap-2 mb-2"><span className="font-bold text-lg">Disponibilidad: </span>
                            { displayedProduct.stock > 0 ? (
                                    <Button size="medium" label="Stock Disponible" outlined severity="success"/>    
                                )  : (
                                    <Button size="medium" label="Stock No Disponible" outlined severity="danger"/>
                                )
                            }
                        </div>
                        
                        <div className="flex align-items-center gap-2"><span className="font-bold text-lg">Comuna</span>
                            {warehouseOptions.length > 1 && (
                                <Dropdown
                                    value={selectedAlmacen}
                                    options={warehouseOptions}
                                    onChange={(e) => setSelectedAlmacen(e.value)}
                                    placeholder="Seleccionar sucursal"
                                />
                            )}
                        </div>
                    </div>

                    <div className="p-3 border-1 border-round surface-border bg-white mt-2">    
                        <div className="flex align-items-center gap-2 mb-3"><span className="font-bold text-lg">Disponibilidad</span></div>
                        <div className="flex flex-column gap-3">
                            <div className="flex gap-3 mb-5">
                                <div className="product-detail-avail-icon"><i className="pi pi-calendar text-primary text-lg"></i></div>
                                <div className="flex flex-column gap-1">
                                    <div className="flex align-items-center gap-2">
                                        <span className="font-semibold text-base">Retiro en sucursal</span>
                                        <i className="pi pi-info-circle text-primary cursor-pointer" data-pr-tooltip="Verifica que tu transporte sea apto para productos sobre 3 metros de largo según Ley de Tránsito N°18.290." data-pr-position="top"></i>
                                    </div>
                                    <span className="text-base font-medium">{selectedSucursalName}</span>
                                  
                                    <span className="text-sm p-text-secondary mt-1">Desde 2 a 72 hrs. hábiles de acuerdo a disponibilidad.</span>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <div className="product-detail-avail-icon"><i className="pi pi-truck text-primary text-xl"></i></div>
                                <div className="flex flex-column gap-1">
                                    <div className="flex align-items-center gap-2">
                                        <span className="font-semibold text-base">Despacho Programado</span>
                                        <i className="pi pi-info-circle text-primary cursor-pointer" data-pr-tooltip="La descarga del material es responsabilidad del cliente." data-pr-position="top"></i>
                                    </div>
                                    <span className="text-sm p-text-secondary mt-1">Fecha estimada, 48 hrs.</span>
                                    <a className="text-sm text-primary font-semibold mt-1 inline-flex align-items-center gap-1" href={`${getCompany().whatsappUrl}&text=Hola,%20necesito%20información%20sobre%20despacho%20programado`} target="_blank" rel="noreferrer noopener">
                                        <i className="pi pi-whatsapp"></i>Consultar disponibilidad
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Tooltip target=".pi-info-circle" />
                </div>
            </div>
        </div>
    );
};

export default ProductDetail;
